
import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Search, Filter, X, BookOpen, GraduationCap, Package } from "lucide-react";

interface Product {
  id: number;
  codigo_produto: string;
  descricao_produto: string;
  utilizacao: string | null;
  ano_letivo: number;
  serie: string;
  sku: string;
  preco: number;
  status: string;
}

interface ProductFiltersProps {
  data: Product[];
  search: string;
  onSearchChange: (search: string) => void;
  selectedFilters: {
    utilizacao: string[];
    ano_letivo: number[];
    serie: string[];
    status: string[];
  };
  onFilterChange: (filterType: string, value: string | number) => void;
  onClearFilters: () => void;
}

export const ProductFilters = ({
  data,
  search,
  onSearchChange,
  selectedFilters,
  onFilterChange,
  onClearFilters
}: ProductFiltersProps) => {
  const getUniqueValues = (key: keyof Product) => {
    const values = data.map(item => item[key]).filter(Boolean);
    return [...new Set(values)];
  };

  const utilizacaoOptions = getUniqueValues('utilizacao') as string[];
  const anoLetivoOptions = getUniqueValues('ano_letivo') as number[];
  const serieOptions = getUniqueValues('serie') as string[];
  const statusOptions = getUniqueValues('status') as string[];

  const totalActiveFilters = Object.values(selectedFilters).flat().length;

  const getUtilizacaoIcon = (utilizacao: string) => {
    if (utilizacao?.toLowerCase() === 'aluno') return BookOpen;
    if (utilizacao?.toLowerCase() === 'professor') return GraduationCap;
    return Package;
  };

  return (
    <Card className="mb-6 shadow-sm">
      <CardContent className="p-6">
        <div className="flex flex-col lg:flex-row gap-6">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Pesquisar produtos, códigos, descrições..."
                value={search}
                onChange={(e) => onSearchChange(e.target.value)}
                className="pl-10 h-12"
              />
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Filtros</span>
              {totalActiveFilters > 0 && (
                <Badge variant="secondary" className="text-xs">
                  {totalActiveFilters}
                </Badge>
              )}
            </div>
            
            {totalActiveFilters > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={onClearFilters}
                className="h-8"
              >
                <X className="h-3 w-3 mr-1" />
                Limpar
              </Button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
          <div className="space-y-2">
            <label className="text-sm font-medium text-muted-foreground">Utilização</label>
            <div className="flex flex-wrap gap-2">
              {utilizacaoOptions.map((utilizacao) => {
                const Icon = getUtilizacaoIcon(utilizacao);
                const isSelected = selectedFilters.utilizacao.includes(utilizacao);
                return (
                  <Button
                    key={utilizacao}
                    variant={isSelected ? "default" : "outline"}
                    size="sm"
                    onClick={() => onFilterChange('utilizacao', utilizacao)}
                    className="h-8 text-xs"
                  >
                    <Icon className="h-3 w-3 mr-1" />
                    {utilizacao}
                  </Button>
                );
              })}
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-muted-foreground">Ano Letivo</label>
            <div className="flex flex-wrap gap-2">
              {anoLetivoOptions.map((ano) => {
                const isSelected = selectedFilters.ano_letivo.includes(ano);
                return (
                  <Button
                    key={ano}
                    variant={isSelected ? "default" : "outline"}
                    size="sm"
                    onClick={() => onFilterChange('ano_letivo', ano)}
                    className="h-8 text-xs"
                  >
                    {ano}
                  </Button>
                );
              })}
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-muted-foreground">Série</label>
            <div className="flex flex-wrap gap-2">
              {serieOptions.map((serie) => {
                const isSelected = selectedFilters.serie.includes(serie);
                return (
                  <Button
                    key={serie}
                    variant={isSelected ? "default" : "outline"}
                    size="sm"
                    onClick={() => onFilterChange('serie', serie)}
                    className="h-8 text-xs"
                  >
                    {serie}
                  </Button>
                );
              })}
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-muted-foreground">Status</label>
            <div className="flex flex-wrap gap-2">
              {statusOptions.map((status) => {
                const isSelected = selectedFilters.status.includes(status);
                return (
                  <Button
                    key={status}
                    variant={isSelected ? "default" : "outline"}
                    size="sm"
                    onClick={() => onFilterChange('status', status)}
                    className="h-8 text-xs"
                  >
                    {status}
                  </Button>
                );
              })}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
